// 배송정보 stale 일괄 반영 RPC 응답(JSONB envelope) 검증 — 순수 함수.
//
// RPC가 RETURNS TABLE이면 PostgREST 응답 행 cap에 결과가 잘릴 수 있다(쓰기는 전부 실행됐는데
// 상태는 앞부분만 도착). v2부터 단일 JSONB envelope {"results":[...]}로 받고, 여기서
// "제출한 모든 id에 허용된 상태가 정확히 1개씩" 왔는지 검증한다. 하나라도 어긋나면
// commit-ambiguous — 쓰기가 됐는지 알 수 없으므로 throw하고 호출부는 재스캔한다
// (블라인드 재시도 금지). (구현 codexloop 합의, 2026-07-29)

export type ShippingSyncApplyStatus =
  | 'applied'
  | 'conflict_stored'
  | 'conflict_items'
  | 'missing'
  | 'empty'
  | 'invalid';

export interface ShippingSyncApplyResult {
  parsedOrderId: number;
  status: ShippingSyncApplyStatus;
}

const ALLOWED_STATUSES: ReadonlySet<string> = new Set([
  'applied',
  'conflict_stored',
  'conflict_items',
  'missing',
  'empty',
  'invalid',
]);

export class ShippingSyncAmbiguousResponseError extends Error {
  constructor(detail: string) {
    super(`배송정보 일괄 반영 응답 검증 실패(commit-ambiguous): ${detail}`);
    this.name = 'ShippingSyncAmbiguousResponseError';
  }
}

export function validateShippingSyncResponse(
  submittedIds: number[],
  payload: unknown
): ShippingSyncApplyResult[] {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new ShippingSyncAmbiguousResponseError('envelope이 객체가 아님');
  }
  const results = (payload as { results?: unknown }).results;
  if (!Array.isArray(results)) {
    throw new ShippingSyncAmbiguousResponseError('results 배열 없음');
  }

  const submitted = new Set(submittedIds);
  if (submitted.size !== submittedIds.length) {
    // 클라 페이로드 자체 버그 — RPC도 중복을 거부하지만 여기서도 막는다.
    throw new ShippingSyncAmbiguousResponseError('제출 id에 중복이 있음');
  }
  if (results.length !== submittedIds.length) {
    throw new ShippingSyncAmbiguousResponseError(
      `결과 수 불일치 (제출 ${submittedIds.length} vs 응답 ${results.length})`
    );
  }

  const seen = new Set<number>();
  const parsed: ShippingSyncApplyResult[] = [];
  for (const entry of results) {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
      throw new ShippingSyncAmbiguousResponseError('결과 항목이 객체가 아님');
    }
    const { parsedOrderId, status } = entry as { parsedOrderId?: unknown; status?: unknown };
    if (typeof parsedOrderId !== 'number' || !Number.isFinite(parsedOrderId)) {
      throw new ShippingSyncAmbiguousResponseError('parsedOrderId가 number가 아님');
    }
    if (typeof status !== 'string' || !ALLOWED_STATUSES.has(status)) {
      throw new ShippingSyncAmbiguousResponseError(`허용되지 않은 상태: ${String(status)}`);
    }
    if (!submitted.has(parsedOrderId)) {
      throw new ShippingSyncAmbiguousResponseError(`제출하지 않은 id: ${parsedOrderId}`);
    }
    if (seen.has(parsedOrderId)) {
      throw new ShippingSyncAmbiguousResponseError(`중복 결과 id: ${parsedOrderId}`);
    }
    seen.add(parsedOrderId);
    parsed.push({ parsedOrderId, status: status as ShippingSyncApplyStatus });
  }

  return parsed;
}
